// frontend/src/components/condo/CondoPortal.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../apiConfig";
import { logout } from "../../auth";
import "../admin/midias.css";
import TvScreenPreview from "../shared/TvScreenPreview";

const LOGO = "/logo-euro-privilege.png";

// Mídia do comunicado: renderiza <video> para MP4/WebM, <img> para imagens
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url || "");
const NoticeMedia = ({ src, alt, isVideo, ...props }) =>
  (isVideo ?? isVideoUrl(src))
    ? <video src={src} muted autoPlay loop playsInline {...props} />
    : <img src={src} alt={alt || ""} {...props} />;

const Icon = ({ name, size = 18, stroke = 1.6, color = "currentColor" }) => {
  const paths = {
    plus:      <><path d="M12 5v14"/><path d="M5 12h14"/></>,
    refresh:   <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10"/><path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"/></>,
    edit:      <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:     <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
    x:         <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    check:     <path d="M20 6L9 17l-5-5"/>,
    calendar:  <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    arrowDown: <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>,
    list:      <><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
    logout:    <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/></>,
    layers:    <><path d="M12 2l9 5-9 5-9-5 9-5z"/><path d="M3 12l9 5 9-5"/><path d="M3 17l9 5 9-5"/></>,
    image:     <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></>,
    monitor:   <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      {paths[name] || null}
    </svg>
  );
};

const Field = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}{required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>{hint}</div>}
  </div>
);

// Limites de vídeo do síndico (iguais aos do backend: /condo/media)
const MAX_VIDEO_MB = 50;
const MAX_VIDEO_SEC = 60;
const MAX_VIDEO_NOTICES = 5; // comunicados em vídeo por condomínio

// Lê largura/altura (e duração, no vídeo) de um arquivo local
function readMediaInfo(file) {
  const url = URL.createObjectURL(file);
  return new Promise((resolve) => {
    const done = (info) => { URL.revokeObjectURL(url); resolve(info); };
    if (file.type.startsWith("video/")) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => done({ width: v.videoWidth, height: v.videoHeight, duration: v.duration });
      v.onerror = () => done({ width: 0, height: 0, duration: 0 });
      v.src = url;
    } else {
      const img = new Image();
      img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight, duration: 0 });
      img.onerror = () => done({ width: 0, height: 0, duration: 0 });
      img.src = url;
    }
  });
}

// Classificação de layout compatível a partir das dimensões (igual ao admin/backend).
function classifyLayouts(width, height) {
  if (!width || !height || width <= 0 || height <= 0) return [];
  if (height > width) return ["layout4"];            // retrato: Tela Cheia
  return ["layout2", "layout3"];                     // paisagem/quadrado: Padrão/Assembleia
}

// Rótulo amigável do formato de uma imagem já enviada.
function imageFormatLabel(asset) {
  const layouts = asset?.compatibleLayouts || (asset?.width && asset?.height ? classifyLayouts(asset.width, asset.height) : []);
  if (layouts.includes("layout4") && !layouts.includes("layout2")) return "Retrato";
  if (layouts.length > 0) return "Paisagem";
  return "—";
}

// Prévia 9:16 de uma tela do condomínio (status de /condo/screens + estimativa de content-preview)
function ScreenTvPreview({ screen, preview, width }) {
  const notice = preview?.noticePreview
    || ((screen.nowPlayingNoticeTitles || []).length > 0 ? { title: screen.nowPlayingNoticeTitles[0], text: "", imageUrl: null } : null);
  return (
    <TvScreenPreview
      width={width}
      layoutType={screen.layoutType}
      isAssembly={!!preview?.isAssembly}
      mediaUrl={preview?.mediaUrl || null}
      mediaType={preview?.mediaType || screen.nowPlayingMediaType || null}
      notice={notice}
      footerUrl={screen.footerMediaUrl || null}
      isOnline={!!screen.isOnline}
    />
  );
}

export default function CondoPortal() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [condoName, setCondoName] = useState("");
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Notice modal
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [nTitle, setNTitle] = useState("");
  const [nText, setNText] = useState("");
  const [nValidFrom, setNValidFrom] = useState("");
  const [nValidTo, setNValidTo] = useState("");
  const [nDuration, setNDuration] = useState("10");
  const [nActive, setNActive] = useState(true);
  const [nImageAsset, setNImageAsset] = useState(null);
  const [nImageFile, setNImageFile] = useState(null); // arquivo pendente (só sobe ao salvar)
  const [uploadingImg, setUploadingImg] = useState(false);
  const [nScope, setNScope] = useState("all"); // 'all' | 'screens'
  const [nScreenIds, setNScreenIds] = useState([]);
  const [condoScreens, setCondoScreens] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // % do envio ao S3 (null = sem envio)
  const [previewLayout, setPreviewLayout] = useState("layout2"); // layout2 | layout4
  // Celular: TV menor nos cards, ao lado do nome (não em cima, ocupando a tela)
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  // Espaço livre da coluna de preview do formulário — a TV cresce até preenchê-lo, sem rolar
  const [formPreviewBox, setFormPreviewBox] = useState({ w: 0, h: 0 });
  const formPreviewObserver = React.useRef(null);
  const formPreviewRef = React.useCallback((el) => {
    if (formPreviewObserver.current) { formPreviewObserver.current.disconnect(); formPreviewObserver.current = null; }
    if (!el || typeof ResizeObserver === "undefined") return;
    formPreviewObserver.current = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      setFormPreviewBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    formPreviewObserver.current.observe(el);
  }, []);

  // Delete confirm
  const [deleteNotice, setDeleteNotice] = useState(null);

  // Painel de telas
  const [view, setView] = useState("comunicados"); // 'comunicados' | 'telas'
  const [previewScreen, setPreviewScreen] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [cardPreviews, setCardPreviews] = useState({}); // prévia de cada tela nos cards { [screenId]: preview }
  const [previewLoading, setPreviewLoading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    loadMe();
    loadScreens();
    loadNotices();
    // eslint-disable-next-line
  }, []);

  // Atualiza o status das telas periodicamente quando o painel está aberto
  useEffect(() => {
    if (view !== "telas") return;
    const iv = setInterval(loadScreens, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [view]);

  // Prévia de cada tela nos cards do painel de telas (auto-atualiza a cada 15s)
  useEffect(() => {
    if (view !== "telas" || condoScreens.length === 0) return;
    let active = true;
    const load = async () => {
      const entries = await Promise.all(condoScreens.map(async (s) => {
        try {
          const r = await fetch(`${API_URL}/condo/screens/${s._id}/content-preview`, { headers: { Authorization: `Bearer ${token}` } });
          return r.ok ? [s._id, (await r.json()).preview || null] : [s._id, null];
        } catch {
          return [s._id, null];
        }
      }));
      if (active) setCardPreviews((prev) => ({ ...prev, ...Object.fromEntries(entries.filter(([, p]) => p)) }));
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { active = false; clearInterval(iv); };
    // eslint-disable-next-line
  }, [view, condoScreens]);

  // Prévia do que está tocando na tela selecionada (auto-atualiza a cada 10s)
  useEffect(() => {
    if (!previewScreen) { setPreviewData(null); return; }
    let active = true;
    const load = async () => {
      setPreviewLoading(true);
      try {
        const r = await fetch(`${API_URL}/condo/screens/${previewScreen._id}/content-preview`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && active) { const d = await r.json(); setPreviewData(d.preview || null); }
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setPreviewLoading(false);
      }
    };
    load();
    const iv = setInterval(load, 10000);
    return () => { active = false; clearInterval(iv); };
    // eslint-disable-next-line
  }, [previewScreen]);

  async function loadMe() {
    try {
      const r = await fetch(`${API_URL}/condo/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setCondoName(data.condoName || "Meu condomínio");
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function loadScreens() {
    try {
      const r = await fetch(`${API_URL}/condo/screens`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setCondoScreens(data.screens || []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function loadNotices() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/condo/notices`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao carregar comunicados");
      }
      const data = await r.json();
      setNotices(data.notices || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Erro ao carregar comunicados");
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  // ── Notice modal ──
  function openCreateNotice() {
    setEditingNotice(null);
    setNTitle("");
    setNText("");
    setNValidFrom("");
    setNValidTo("");
    setNDuration("10");
    setNActive(true);
    setNImageAsset(null);
    setNImageFile(null);
    setNScope("all");
    setNScreenIds([]);
    setShowNoticeModal(true);
  }

  function openEditNotice(notice) {
    setEditingNotice(notice);
    setNTitle(notice.title || "");
    setNText(notice.text || "");
    setNValidFrom(notice.validFrom ? new Date(notice.validFrom).toISOString().slice(0, 10) : "");
    setNValidTo(notice.validTo ? new Date(notice.validTo).toISOString().slice(0, 10) : "");
    setNDuration(String(notice.displayDurationSec || 10));
    setNActive(notice.active);
    setNImageAsset(notice.imageAssetId || null);
    setNImageFile(null);
    if (notice.scopeType === "SCREEN" && notice.scopeRef) {
      const ids = Array.isArray(notice.scopeRef) ? notice.scopeRef : [notice.scopeRef];
      setNScope("screens");
      setNScreenIds(ids.map(String));
    } else {
      setNScope("all");
      setNScreenIds([]);
    }
    setShowNoticeModal(true);
  }

  // Seleciona a imagem/vídeo localmente (preview) — o upload ao S3 só acontece ao salvar.
  async function handleImageSelect(file) {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    if (!isVideo && !file.type.startsWith("image/")) return alert("Selecione uma imagem ou um vídeo");
    if (isVideo) {
      // Confere o limite antes de enviar (o backend confere de novo ao salvar)
      const videoNotices = notices.filter((n) => n.editable && n.imageAssetId?.type === "VIDEO" && n._id !== editingNotice?._id).length;
      if (videoNotices >= MAX_VIDEO_NOTICES) return alert(`Seu condomínio já tem ${MAX_VIDEO_NOTICES} comunicados em vídeo, que é o máximo. Exclua um deles ou use uma imagem.`);
    }
    if (isVideo && file.size > MAX_VIDEO_MB * 1024 * 1024) return alert(`O vídeo tem ${Math.round(file.size / 1024 / 1024)} MB. O máximo é ${MAX_VIDEO_MB} MB.`);
    setUploadingImg(true);
    try {
      const dims = await readMediaInfo(file);
      if (isVideo && dims.duration > MAX_VIDEO_SEC + 0.5) {
        alert(`O vídeo tem ${Math.round(dims.duration)} segundos. O máximo é ${MAX_VIDEO_SEC} segundos.`);
        return;
      }
      // Revoga preview local anterior, se houver
      if (nImageAsset?._localUrl) URL.revokeObjectURL(nImageAsset._localUrl);
      const localUrl = URL.createObjectURL(file);
      setNImageFile(file);
      setNImageAsset({
        _local: true,
        _localUrl: localUrl,
        publicUrl: localUrl,
        type: isVideo ? "VIDEO" : "IMAGE",
        durationSec: isVideo ? Math.round(dims.duration) || null : null,
        name: file.originalname || file.name || (isVideo ? "Vídeo" : "Imagem"),
        width: dims.width || null,
        height: dims.height || null,
        compatibleLayouts: classifyLayouts(dims.width, dims.height),
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao carregar arquivo");
    } finally {
      setUploadingImg(false);
    }
  }

  function clearImage() {
    if (nImageAsset?._localUrl) URL.revokeObjectURL(nImageAsset._localUrl);
    setNImageAsset(null);
    setNImageFile(null);
  }

  // Sobe o arquivo pendente direto ao S3 (sem passar pelo servidor) e retorna o MediaAsset criado:
  // presign → PUT no S3 com progresso → confirm (o backend confere e registra a mídia)
  async function uploadPendingImage(file) {
    const info = await readMediaInfo(file);
    const contentType = file.type || (file.name.toLowerCase().endsWith(".mp4") ? "video/mp4" : "application/octet-stream");

    const pr = await fetch(`${API_URL}/condo/media/presign`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ fileName: file.name, contentType, size: file.size, durationSec: info.duration || null }),
    });
    const presign = await pr.json().catch(() => ({}));
    if (!pr.ok) throw new Error(presign.message || "Erro ao preparar envio");

    setUploadProgress(0);
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener("progress", (e) => { if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100)); });
      xhr.addEventListener("load", () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Falha no envio do arquivo (${xhr.status})`))));
      xhr.addEventListener("error", () => reject(new Error("Falha de rede ao enviar o arquivo")));
      xhr.open("PUT", presign.uploadUrl);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.send(file);
    });

    const cr = await fetch(`${API_URL}/condo/media/confirm`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ s3Key: presign.s3Key, name: file.name, width: info.width || null, height: info.height || null, durationSec: info.duration || null }),
    });
    const data = await cr.json().catch(() => ({}));
    if (!cr.ok) throw new Error(data.message || "Erro ao registrar arquivo");
    return data.mediaAsset;
  }

  async function handleSaveNotice() {
    if (!nTitle.trim()) return alert("Título obrigatório");
    if (!nText.trim() && !nImageAsset) return alert("Adicione um texto, uma imagem ou um vídeo");
    if (nScope === "screens" && nScreenIds.length === 0) return alert("Selecione ao menos uma tela");

    setSaving(true);
    try {
      // Só agora o arquivo pendente é enviado ao S3 / vira MediaAsset.
      let imageAssetId = nImageAsset?._id || null;
      if (nImageFile) {
        const uploaded = await uploadPendingImage(nImageFile).finally(() => setUploadProgress(null));
        imageAssetId = uploaded?._id || null;
      }
      const payload = {
        title: nTitle,
        text: nText,
        imageAssetId,
        validFrom: nValidFrom || null,
        validTo: nValidTo || null,
        displayDurationSec: Number(nDuration) || 10,
        active: nActive,
        screenIds: nScope === "screens" ? nScreenIds : [],
      };
      const url = editingNotice ? `${API_URL}/condo/notices/${editingNotice._id}` : `${API_URL}/condo/notices`;
      const method = editingNotice ? "PUT" : "POST";
      const r = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(payload) });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao salvar comunicado");
      }
      setShowNoticeModal(false);
      await loadNotices();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao salvar comunicado");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteNotice(id) {
    try {
      const r = await fetch(`${API_URL}/condo/notices/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao deletar");
      }
      setDeleteNotice(null);
      await loadNotices();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao deletar comunicado");
    }
  }

  async function handleToggleActive(notice, nextActive) {
    setNotices((prev) => prev.map((n) => (n._id === notice._id ? { ...n, active: nextActive } : n)));
    try {
      const r = await fetch(`${API_URL}/condo/notices/${notice._id}`, {
        method: "PUT", headers: authHeaders, body: JSON.stringify({ active: nextActive }),
      });
      if (!r.ok) throw new Error("Erro ao atualizar");
    } catch (e) {
      setNotices((prev) => prev.map((n) => (n._id === notice._id ? { ...n, active: notice.active } : n)));
      alert(e.message || "Erro ao atualizar comunicado");
    }
  }

  function formatDate(d) {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return "—"; }
  }

  function formatDateTime(d) {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  }

  // "há 5 min", "há 3 h", "há 2 dias"
  function timeAgo(d) {
    const diff = Math.max(0, Date.now() - new Date(d).getTime());
    const min = Math.round(diff / 60000);
    if (min < 1) return "agora há pouco";
    if (min < 60) return `há ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `há ${h} h`;
    const days = Math.round(h / 24);
    return `há ${days} dia${days > 1 ? "s" : ""}`;
  }

  // Telas que receberão o comunicado, conforme o escopo escolhido no formulário.
  function targetScreens() {
    if (nScope === "screens") {
      const ids = new Set(nScreenIds.map(String));
      return condoScreens.filter((s) => ids.has(String(s._id)));
    }
    return condoScreens;
  }

  // Telas cujo layout é incompatível com a imagem (só faz sentido em comunicado só-imagem).
  function imageLayoutMismatch() {
    if (!nImageAsset || nText.trim()) return []; // com texto a imagem é miniatura → sempre cabe
    const compatible = nImageAsset.compatibleLayouts
      || (nImageAsset.width && nImageAsset.height ? classifyLayouts(nImageAsset.width, nImageAsset.height) : []);
    if (compatible.length === 0) return [];
    return targetScreens().filter((s) => !compatible.includes(s.layoutType || "layout2"));
  }

  // Situação do comunicado em palavras simples (considera ligado/desligado e o período)
  function noticeStatus(n) {
    const now = Date.now();
    if (!n.active) return { key: "off", label: "Desligado", badge: "badge-gray" };
    if (n.validFrom && new Date(n.validFrom).getTime() > now) return { key: "scheduled", label: `Começa ${formatDate(n.validFrom)}`, badge: "badge-blue" };
    if (n.validTo && new Date(n.validTo).getTime() < now) return { key: "ended", label: "Encerrado", badge: "badge-gray" };
    return { key: "live", label: "No ar", badge: "badge-green" };
  }

  function periodLabel(n) {
    if (n.validFrom && n.validTo) return `${formatDate(n.validFrom)} até ${formatDate(n.validTo)}`;
    if (n.validTo) return `Até ${formatDate(n.validTo)}`;
    if (n.validFrom) return `A partir de ${formatDate(n.validFrom)}`;
    return "Sem data para acabar";
  }

  // Texto puro para o resumo (comunicados da administração podem vir em HTML)
  function plainText(t) {
    if (!t) return "";
    return String(t).replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
  }

  const visibleNotices = [...notices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const activeCount = notices.filter((n) => noticeStatus(n).key === "live").length;
  const onlineCount = condoScreens.filter((s) => s.isOnline).length;

  return (
    <div className="midias-page condo-portal" style={{ maxWidth: 1000, margin: "0 auto", padding: "28px clamp(14px, 4vw, 24px) 40px" }}>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <img src={LOGO} alt="Euro Privilege" style={{ height: 38, width: "auto", alignSelf: "flex-start" }} />
          <div>
            <h1 className="page-title">{condoName || "…"}</h1>
            <div className="page-sub" style={{ flexWrap: "wrap" }}>
              <span className="live-dot"/>
              <span>
                <b style={{ color: "var(--fg)" }}>{activeCount}</b> comunicado{activeCount !== 1 ? "s" : ""} no ar
                {condoScreens.length > 0 && <> · <b style={{ color: "var(--fg)" }}>{onlineCount}</b> de {condoScreens.length} tela{condoScreens.length !== 1 ? "s" : ""} funcionando</>}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={() => { loadNotices(); loadScreens(); }} title="Atualizar"><Icon name="refresh" size={14}/> Atualizar</button>
          {view === "comunicados" && <button className="btn btn-primary" onClick={openCreateNotice}><Icon name="plus" size={14}/> Novo comunicado</button>}
          <button className="btn" onClick={handleLogout} title="Sair"><Icon name="logout" size={14}/> Sair</button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: "12px 16px", background: "var(--red-soft)", border: "0.5px solid var(--red)", borderRadius: 10, color: "var(--red)", fontSize: 13 }}>{error}</div>
      )}

      {/* Abas de visão */}
      <div style={{ display: "inline-flex", padding: 3, gap: 3, marginTop: 4, background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 12, alignSelf: "flex-start" }}>
        {[
          { id: "comunicados", icon: "list", label: "Comunicados", n: notices.length },
          { id: "telas", icon: "monitor", label: "Telas", n: condoScreens.length },
        ].map((t) => {
          const on = view === t.id;
          return (
            <button key={t.id} onClick={() => setView(t.id)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer", font: "inherit", fontSize: 13, fontWeight: on ? 600 : 500, background: on ? "var(--surface)" : "transparent", color: on ? "var(--fg)" : "var(--fg-3)", boxShadow: on ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
              <Icon name={t.icon} size={14}/> {t.label}
              <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: on ? "var(--accent-soft)" : "transparent", color: on ? "var(--accent)" : "var(--fg-4)" }}>{t.n}</span>
            </button>
          );
        })}
      </div>

      {view === "comunicados" && (() => {
        const mine = visibleNotices.filter((n) => n.editable);
        const fromAdmin = visibleNotices.filter((n) => !n.editable);

        const renderNotice = (notice) => {
          const st = noticeStatus(notice);
          const excerpt = plainText(notice.text);
          const thumb = notice.imageAssetId?.publicUrl;
          const isVideo = notice.imageAssetId?.type === "VIDEO";
          return (
            <div key={notice._id} className={`cp-notice${notice.editable ? "" : " readonly"}${notice.active ? "" : " off"}`}>
              <div className="cp-notice-thumb">
                {thumb
                  ? <NoticeMedia src={thumb} isVideo={isVideo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}/>
                  : <Icon name="list" size={18}/>}
                {isVideo && <span className="cp-notice-video">▶</span>}
              </div>
              <div className="cp-notice-body">
                <div className="cp-notice-title">
                  <span>{notice.title}</span>
                  <span className={`badge ${st.badge}`}><span className="badge-dot"/>{st.label}</span>
                </div>
                <div className="cp-notice-excerpt">{excerpt || (thumb ? (isVideo ? "Comunicado em vídeo" : "Comunicado com imagem") : "—")}</div>
                <div className="cp-notice-meta">
                  <span><Icon name="calendar" size={11} color="var(--fg-4)"/> {periodLabel(notice)}</span>
                  <span>{notice.scopeType === "SCREEN"
                    ? `${Array.isArray(notice.scopeRef) ? notice.scopeRef.length : 1} de ${condoScreens.length || "?"} telas`
                    : "Todas as telas"}</span>
                  <span>{notice.displayDurationSec || 10}s na tela</span>
                </div>
              </div>
              {notice.editable && (
                <div className="cp-notice-actions">
                  <label title={notice.active ? "Tirar do ar" : "Colocar no ar"} onClick={() => handleToggleActive(notice, !notice.active)} className="cp-switch-label">
                    <span role="switch" aria-checked={!!notice.active} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); handleToggleActive(notice, !notice.active); } }}
                      className={`cp-switch${notice.active ? " on" : ""}`}><span/></span>
                    <span>{notice.active ? "Ligado" : "Desligado"}</span>
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="midia-act purple" title="Editar" aria-label="Editar" onClick={() => openEditNotice(notice)}><Icon name="edit" size={14}/></button>
                    <button className="midia-act red" title="Excluir" aria-label="Excluir" onClick={() => setDeleteNotice(notice)}><Icon name="trash" size={14}/></button>
                  </div>
                </div>
              )}
            </div>
          );
        };

        return (
        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 28 }}>
          {loading ? (
            <div style={{ padding: "50px 20px", textAlign: "center", color: "var(--fg-3)" }}>Carregando…</div>
          ) : (
            <>
              {/* Comunicados do síndico */}
              <section>
                <div className="cp-section-h">
                  <h3>Seus comunicados <span>{mine.length}</span></h3>
                  <p>Criados por você. Ligue ou desligue cada um nas TVs quando quiser.</p>
                </div>
                {mine.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center", border: "1.5px dashed var(--hairline)", borderRadius: 14, color: "var(--fg-3)", fontSize: 13, background: "var(--surface)" }}>
                    <Icon name="list" size={26} color="var(--fg-4)"/>
                    <div style={{ marginTop: 8, fontWeight: 600, color: "var(--fg-2)" }}>Você ainda não criou nenhum comunicado</div>
                    <div style={{ marginTop: 4 }}>Avise os moradores sobre manutenção, eventos, regras… Ele aparece nas TVs do condomínio.</div>
                    <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={openCreateNotice}><Icon name="plus" size={13}/> Criar primeiro comunicado</button>
                  </div>
                ) : (
                  <div className="cp-list">{mine.map(renderNotice)}</div>
                )}
              </section>

              {/* Comunicados da administração (somente leitura) */}
              {fromAdmin.length > 0 && (
                <section>
                  <div className="cp-section-h">
                    <h3>Da administração <span>{fromAdmin.length}</span></h3>
                    <p>Enviados pela Euro Condomínios para as TVs do seu condomínio. Só a administração pode alterar.</p>
                  </div>
                  <div className="cp-list">{fromAdmin.map(renderNotice)}</div>
                </section>
              )}
            </>
          )}
        </div>
        );
      })()}

      {view === "telas" && (() => {
        const offline = condoScreens.filter((s) => !s.isOnline);
        const label = { fontSize: 10.5, fontWeight: 600, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em" };
        return (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Aviso só quando alguma tela está sem conexão (o cabeçalho já diz quantas funcionam) */}
          {offline.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 12, background: "var(--amber-soft)", color: "var(--fg)" }}>
              <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--amber)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700 }}>!</span>
              <div style={{ minWidth: 0, fontSize: 13, lineHeight: 1.45 }}>
                <b>{offline.length === 1 ? `A tela "${offline[0].name || "sem nome"}" está sem conexão.` : `${offline.length} telas estão sem conexão.`}</b>
                {" "}Confira se a TV está ligada e com internet. Se continuar, fale com a administração.
              </div>
            </div>
          )}

          {condoScreens.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--fg-3)", fontSize: 13, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 14 }}>
              <Icon name="monitor" size={28} color="var(--fg-4)"/>
              <div style={{ marginTop: 8, fontWeight: 600, color: "var(--fg-2)" }}>Nenhuma tela por aqui ainda</div>
              <div style={{ marginTop: 4 }}>Quando a administração instalar uma TV no condomínio, ela aparece nesta página.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(440px, 100%), 1fr))", gap: 14 }}>
              {condoScreens.map((s) => {
                const pv = cardPreviews[s._id];
                const titles = pv?.noticeTitles || s.nowPlayingNoticeTitles || [];
                const current = pv?.noticePreview?.title || titles[0] || null;
                // Fila: os próximos depois do atual, na ordem de rotação
                const idx = Math.max(0, titles.indexOf(current));
                const upcoming = titles.length > 1 ? [...titles.slice(idx + 1), ...titles.slice(0, idx)] : [];
                const mediaNow = pv?.mediaTitle || s.nowPlayingMediaTitle || pv?.campaignName || s.nowPlayingCampaignName || null;
                const showsMedia = (s.layoutType || "layout2") !== "layout4" && !pv?.isAssembly;
                return (
                  <button key={s._id} className="condo-screen-card cp-screen" onClick={() => setPreviewScreen(s)}>
                    <div className="cp-screen-tv"><ScreenTvPreview screen={s} preview={pv} width={isNarrow ? 118 : 170}/></div>
                    <div className="cp-screen-head">
                      <div className="cp-screen-name">{s.name || "Tela sem nome"}</div>
                      <div className={`cp-pill ${s.isOnline ? "ok" : "warn"}`}>
                        <span/>{s.isOnline ? "Funcionando" : `Sem conexão${s.lastSeenAt ? ` ${timeAgo(s.lastSeenAt)}` : ""}`}
                      </div>
                    </div>
                    <div className="cp-screen-now">
                      <div style={label}>Passando agora</div>
                      <div className="cp-screen-now-title" style={{ color: current ? "var(--fg)" : "var(--fg-4)" }}>{current || "Nenhum comunicado"}</div>
                      {showsMedia && <div className="cp-screen-now-sub">Parceiro: {mediaNow || "—"}</div>}
                    </div>
                    {upcoming.length > 0 && (
                      <div className="cp-screen-next">
                        <div style={label}>Em seguida</div>
                        <ol>
                          {upcoming.slice(0, 4).map((t, i) => <li key={i}><span>{i + 1}</span><span>{t}</span></li>)}
                        </ol>
                        {upcoming.length > 4 && <div className="cp-screen-more">+ {upcoming.length - 4} outro{upcoming.length - 4 > 1 ? "s" : ""}</div>}
                      </div>
                    )}
                    <div className="cp-screen-foot">
                      <span>{titles.length} comunicado{titles.length !== 1 ? "s" : ""} nesta tela</span>
                      <b>Ver ampliada →</b>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      <div style={{ height: 24 }}/>

      {/* ── Modal comunicado ── */}
      {showNoticeModal && (
        <div onClick={() => setShowNoticeModal(false)} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(900px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{editingNotice ? "Editar comunicado" : "Novo comunicado"}</h2>
              <button className="icon-btn" onClick={() => setShowNoticeModal(false)}><Icon name="x" size={16}/></button>
            </div>
            <div style={{ display: "flex", flex: 1, minHeight: 0, flexWrap: "wrap", overflowY: "auto" }}>
            <div style={{ flex: "1 1 360px", minWidth: "min(300px, 100%)", padding: "20px clamp(16px, 4vw, 24px)", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>
              <Field label="Título" required>
                <div className="input">
                  <input className="search-bare-input" type="text" maxLength={60} placeholder="Ex: Manutenção do elevador" value={nTitle} onChange={(e) => setNTitle(e.target.value)} disabled={saving}/>
                </div>
              </Field>
              <Field label="Mensagem" hint="Obrigatório se não houver imagem ou vídeo.">
                <textarea value={nText} onChange={(e) => setNText(e.target.value)} disabled={saving} rows={5} placeholder="Digite o comunicado…" style={{ width: "100%", resize: "vertical", padding: "10px 12px", borderRadius: 10, border: "0.5px solid var(--hairline)", background: "var(--surface)", color: "var(--fg)", font: "inherit", fontSize: 13, lineHeight: 1.5 }}/>
              </Field>
              <Field label="Imagem ou vídeo" hint={`Opcional. Formato recomendado: retrato 1080×1920 px — imagem PNG/JPG ou vídeo MP4 de até ${MAX_VIDEO_SEC}s e ${MAX_VIDEO_MB} MB (máximo de ${MAX_VIDEO_NOTICES} comunicados em vídeo). Com texto, a mídia entra como miniatura ao lado.`}>
                {nImageAsset ? (
                  <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: "var(--accent-soft)", border: "0.5px solid color-mix(in oklch, var(--accent) 30%, transparent)", borderRadius: 10 }}>
                    <NoticeMedia src={nImageAsset.publicUrl} isVideo={nImageAsset.type === "VIDEO"} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nImageAsset.name || (nImageAsset.type === "VIDEO" ? "Vídeo" : "Imagem")}</div>
                      <div style={{ fontSize: 11, color: "var(--fg-3)" }}>
                        {nImageAsset.type === "VIDEO" ? "Vídeo · " : ""}{imageFormatLabel(nImageAsset)}{nImageAsset.width && nImageAsset.height ? ` · ${nImageAsset.width}×${nImageAsset.height}` : ""}{nImageAsset.type === "VIDEO" && nImageAsset.durationSec ? ` · ${nImageAsset.durationSec}s` : ""}{nImageAsset._local ? " · será enviado ao salvar" : ""}
                      </div>
                    </div>
                    <button onClick={clearImage} disabled={saving} style={{ border: "none", background: "transparent", color: "var(--red)", fontSize: 12, cursor: "pointer" }}>Remover</button>
                  </div>
                  {(() => {
                    const mismatch = imageLayoutMismatch();
                    if (mismatch.length === 0) return null;
                    return (
                      <div style={{ display: "flex", gap: 8, padding: "8px 12px", background: "color-mix(in oklch, var(--amber) 12%, transparent)", border: "0.5px solid color-mix(in oklch, var(--amber) 35%, transparent)", borderRadius: 10, fontSize: 11.5, color: "var(--amber)", lineHeight: 1.45 }}>
                        <Icon name="image" size={14}/>
                        <span>Imagem em <strong>{imageFormatLabel(nImageAsset).toLowerCase()}</strong> pode não preencher {mismatch.length} tela(s): {mismatch.slice(0, 3).map((s) => s.name || "sem nome").join(", ")}{mismatch.length > 3 ? "…" : ""}. Prefira retrato (1080×1920) ou adicione um texto.</span>
                      </div>
                    );
                  })()}
                  </>
                ) : (
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px", border: "1.5px dashed var(--hairline)", borderRadius: 10, cursor: uploadingImg ? "wait" : "pointer", color: "var(--fg-3)", fontSize: 13 }}>
                    <Icon name="image" size={16}/>
                    {uploadingImg ? "Carregando…" : "Escolher imagem ou vídeo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime,video/webm" style={{ display: "none" }} disabled={uploadingImg || saving} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleImageSelect(f); }}/>
                  </label>
                )}
              </Field>
              <Field label="Onde exibir" hint={nScope === "screens" ? `${nScreenIds.length} de ${condoScreens.length} tela(s) selecionada(s)` : "O comunicado aparece em todas as telas do condomínio."}>
                <div className="input">
                  <select className="hw-bare-select" value={nScope} onChange={(e) => setNScope(e.target.value)} disabled={saving}>
                    <option value="all">Todas as telas do condomínio</option>
                    <option value="screens">Telas específicas</option>
                  </select>
                  <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
                </div>
              </Field>
              {nScope === "screens" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                  {condoScreens.length === 0 ? (
                    <div style={{ padding: 12, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhuma tela cadastrada neste condomínio</div>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>Selecione as telas</span>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button type="button" onClick={() => setNScreenIds(condoScreens.map((s) => String(s._id)))} disabled={saving || nScreenIds.length === condoScreens.length} style={{ border: "none", background: "transparent", color: nScreenIds.length === condoScreens.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Todas</button>
                          {nScreenIds.length > 0 && <button type="button" onClick={() => setNScreenIds([])} disabled={saving} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Limpar</button>}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 180, overflowY: "auto" }}>
                        {condoScreens.map((s) => {
                          const id = String(s._id);
                          const on = nScreenIds.includes(id);
                          return (
                            <button type="button" key={id} onClick={() => setNScreenIds(on ? nScreenIds.filter((x) => x !== id) : [...nScreenIds, id])} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "pointer" }}>
                              <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on && <Icon name="check" size={10} color="white"/>}</span>
                              <span style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{s.name || "Tela sem nome"}</span>
                            </button>
                          );
                        })}
                      </div>
                      {nScreenIds.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--amber)" }}>Selecione ao menos uma tela.</div>
                      )}
                    </>
                  )}
                </div>
              )}
              {/* minmax(0,1fr) + minWidth 0: o input de data não estoura a coluna no celular.
                  Sem ícone extra: o próprio campo de data já mostra o calendário */}
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
                <Field label="Válido de">
                  <div className="input">
                    <input className="search-bare-input" type="date" value={nValidFrom} onChange={(e) => setNValidFrom(e.target.value)} disabled={saving} style={{ minWidth: 0, width: "100%" }}/>
                  </div>
                </Field>
                <Field label="Válido até">
                  <div className="input">
                    <input className="search-bare-input" type="date" value={nValidTo} onChange={(e) => setNValidTo(e.target.value)} disabled={saving} style={{ minWidth: 0, width: "100%" }}/>
                  </div>
                </Field>
              </div>
              <Field label="Duração na tela (segundos)">
                <div className="input">
                  <input className="search-bare-input" type="number" min="3" value={nDuration} onChange={(e) => setNDuration(e.target.value)} disabled={saving}/>
                </div>
              </Field>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 10, cursor: "default" }}>
                <div onClick={() => !saving && setNActive(!nActive)} style={{ width: 36, height: 20, borderRadius: 999, background: nActive ? "var(--green)" : "var(--surface-sunken)", border: "0.5px solid var(--hairline)", position: "relative", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 2, left: nActive ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left .15s ease" }}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Colocar no ar</div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{nActive ? "Será exibido nas telas do condomínio" : "Salvo como rascunho"}</div>
                </div>
              </label>
            </div>
            {/* ── Coluna de preview — réplica do player (TvScreenPreview) ── */}
            {(() => {
              const hasImage = !!nImageAsset?.publicUrl;
              const hasText = !!nText.trim();
              const contentType = hasImage && hasText ? "TEXT_IMAGE" : hasImage ? "IMAGE_ONLY" : "TEXT_ONLY";

              const imgCompatible = nImageAsset?.compatibleLayouts
                || (nImageAsset?.width && nImageAsset?.height ? classifyLayouts(nImageAsset.width, nImageAsset.height) : null);
              const availableLayouts = (() => {
                const all = ["layout2", "layout4"];
                if (contentType !== "IMAGE_ONLY" || !imgCompatible) return all;
                const filtered = all.filter((lk) => imgCompatible.includes(lk));
                return filtered.length > 0 ? filtered : all;
              })();
              const safeLayout = availableLayouts.includes(previewLayout) ? previewLayout : availableLayouts[0];
              const LAYOUT_LABELS = { layout2: "Padrão", layout4: "Tela Cheia" };

              // Rodapé real de uma das telas que vão receber o comunicado
              const footerUrl = (targetScreens().find((sc) => sc.footerMediaUrl) || condoScreens.find((sc) => sc.footerMediaUrl))?.footerMediaUrl || null;

              // Maior tela 9:16 que cabe no espaço livre da coluna
              const fit = Math.min(formPreviewBox.w, formPreviewBox.h * 540 / 960);
              const deviceW = fit > 0 ? Math.floor(fit) : 200;

              return (
                <div style={{ flex: "1 1 260px", minWidth: 240, overflow: "hidden", minHeight: 0, background: "var(--surface-sunken)", borderLeft: "0.5px solid var(--hairline)", padding: "20px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Preview</div>
                    <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 1 }}>Como aparece nas telas</div>
                  </div>

                  {/* Seletor de layout */}
                  <div style={{ flexShrink: 0, display: "flex", padding: 2, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 8, overflow: "hidden" }}>
                    {availableLayouts.map((lk, i) => {
                      const isActive = safeLayout === lk;
                      return (
                        <button key={lk} type="button" onClick={() => setPreviewLayout(lk)}
                          style={{ flex: 1, padding: "4px 0", fontSize: 11, border: "none", borderLeft: i > 0 ? "0.5px solid var(--hairline)" : "none", background: isActive ? "var(--accent)" : "transparent", color: isActive ? "#fff" : "var(--fg-3)", cursor: "pointer", fontWeight: isActive ? 700 : 400 }}>
                          {LAYOUT_LABELS[lk]}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tela retrato — réplica do player (mesmo componente do admin e da aba Telas) */}
                  <div ref={formPreviewRef} style={{ flex: 1, minHeight: 360, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TvScreenPreview
                      width={deviceW}
                      layoutType={safeLayout}
                      notice={{
                        title: nTitle || "Título do comunicado",
                        text: hasText ? nText : (hasImage ? "" : "Texto da mensagem…"),
                        imageUrl: nImageAsset?.publicUrl || null,
                        mediaType: nImageAsset?.type || null,
                      }}
                      footerUrl={footerUrl}
                      mediaPlaceholder={"Conteúdo\ndo parceiro"}
                    />
                  </div>

                  <div style={{ flexShrink: 0, textAlign: "center", fontSize: 11, color: "var(--fg-3)", lineHeight: 1.45 }}>
                    {contentType === "IMAGE_ONLY" ? "Só imagem — ocupa o card inteiro" : contentType === "TEXT_IMAGE" ? "Texto + imagem ao lado" : "Só texto"}
                  </div>
                </div>
              );
            })()}
            </div>{/* fim linha flex */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <button className="btn" onClick={() => setShowNoticeModal(false)} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveNotice} disabled={saving || uploadingImg || !nTitle.trim() || (!nText.trim() && !nImageAsset) || (nScope === "screens" && nScreenIds.length === 0)}>
                {saving ? (uploadProgress != null ? `Enviando ${uploadProgress}%…` : "Salvando…") : <><Icon name="check" size={14}/> {editingNotice ? "Salvar" : "Criar"}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar exclusão de comunicado ── */}
      {deleteNotice && (
        <div onClick={() => setDeleteNotice(null)} style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(420px, 100%)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, padding: 24, boxShadow: "var(--shadow-md)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600 }}>Excluir comunicado</h3>
            <p style={{ fontSize: 13, color: "var(--fg-2)", marginBottom: 20 }}>Excluir <strong>"{deleteNotice.title}"</strong>? Esta ação não pode ser desfeita.</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setDeleteNotice(null)}>Cancelar</button>
              <button className="btn" onClick={() => handleDeleteNotice(deleteNotice._id)} style={{ background: "var(--red-soft)", color: "var(--red)", borderColor: "transparent" }}><Icon name="trash" size={13}/> Excluir</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal: o que a tela está exibindo ── */}
      {previewScreen && (() => {
        const s = condoScreens.find((x) => x._id === previewScreen._id) || previewScreen;
        const np = previewData?.noticePreview || null;
        const titles = previewData?.noticeTitles || s.nowPlayingNoticeTitles || [];
        const showsMedia = (s.layoutType || "layout2") !== "layout4" && !previewData?.isAssembly;
        const sectionLabel = { fontSize: 10.5, fontWeight: 600, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 };
        const hasTech = s.cpuTemp != null || s.cpuUsagePct != null || s.memoryUsedMb != null || s.diskFreeGb != null;
        return (
        <div onClick={() => setPreviewScreen(null)} style={{ position: "fixed", inset: 0, zIndex: 350, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(780px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>{s.name || "Tela"}</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 12.5, color: s.isOnline ? "var(--green)" : "var(--amber)" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor" }}/>
                  {s.isOnline ? "Funcionando" : `Sem conexão${s.lastSeenAt ? ` ${timeAgo(s.lastSeenAt)}` : ""}`}
                  {previewLoading && <span style={{ color: "var(--fg-4)", marginLeft: 8 }}>atualizando…</span>}
                </div>
              </div>
              <button className="icon-btn" onClick={() => setPreviewScreen(null)}><Icon name="x" size={16}/></button>
            </div>

            <div style={{ padding: 22, overflowY: "auto", flex: 1, minHeight: 0, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
              {/* Prévia da tela, igual à TV */}
              <div style={{ flex: "0 0 auto", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <ScreenTvPreview screen={s} preview={previewData} width={260}/>
                <div style={{ fontSize: 11, color: "var(--fg-4)", textAlign: "center", maxWidth: 260 }}>Prévia aproximada, pela programação atual</div>
              </div>

              <div style={{ flex: "1 1 280px", minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>
                {s.isOnline === false && (
                  <div style={{ padding: "10px 12px", background: "var(--amber-soft)", borderRadius: 10, fontSize: 12.5, color: "var(--fg-2)", lineHeight: 1.5 }}>
                    Esta TV não se comunica com o sistema{s.lastSeenAt ? ` desde ${formatDateTime(s.lastSeenAt)}` : ""}. Confira se ela está ligada e com internet.
                  </div>
                )}

                <div>
                  <div style={sectionLabel}>Comunicado na tela {previewData?.isAssembly && <span className="badge badge-amber" style={{ marginLeft: 4, textTransform: "none", letterSpacing: 0 }}>Assembleia</span>}</div>
                  {np ? (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{np.title || "Comunicado"}</div>
                      {np.text && <div style={{ fontSize: 12.5, color: "var(--fg-2)", marginTop: 4, lineHeight: 1.5 }}>{np.text.replace(/<[^>]+>/g, " ").substring(0, 220)}{np.text.length > 220 ? "…" : ""}</div>}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: "var(--fg-4)" }}>Nenhum comunicado no momento.</div>
                  )}
                </div>

                {titles.length > 1 && (
                  <div>
                    <div style={sectionLabel}>Revezando nesta tela ({titles.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {titles.map((t, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t === np?.title ? "var(--fg)" : "var(--fg-2)", fontWeight: t === np?.title ? 600 : 400 }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: t === np?.title ? "var(--green)" : "var(--hairline-strong)", flexShrink: 0 }}/>
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t}</span>
                          {t === np?.title && <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 500 }}>agora</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showsMedia && (
                  <div>
                    <div style={sectionLabel}>Parceiro na tela</div>
                    {previewData?.mediaTitle || previewData?.campaignName ? (
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{previewData.mediaTitle || previewData.campaignName}</div>
                    ) : (
                      <div style={{ fontSize: 13, color: "var(--fg-4)" }}>Nenhum parceiro no momento.</div>
                    )}
                  </div>
                )}

                {hasTech && (
                  <details style={{ fontSize: 12, color: "var(--fg-3)" }}>
                    <summary style={{ cursor: "pointer", userSelect: "none" }}>Detalhes do aparelho</summary>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      {s.cpuTemp != null && <span className="badge badge-gray">Temperatura {Math.round(s.cpuTemp)}°C</span>}
                      {s.cpuUsagePct != null && <span className="badge badge-gray">Processador {Math.round(s.cpuUsagePct)}%</span>}
                      {s.memoryUsedMb != null && s.memoryTotalMb != null && <span className="badge badge-gray">Memória {Math.round((s.memoryUsedMb / s.memoryTotalMb) * 100)}%</span>}
                      {s.diskFreeGb != null && <span className="badge badge-gray">{Math.round(s.diskFreeGb)} GB livres</span>}
                      {s.lastSeenAt && <span className="badge badge-gray">Último sinal {formatDateTime(s.lastSeenAt)}</span>}
                    </div>
                  </details>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <span style={{ fontSize: 11.5, color: "var(--fg-4)" }}>Atualiza sozinho a cada 10 segundos</span>
              <button className="btn btn-primary" onClick={() => setPreviewScreen(null)}>Fechar</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
